import { useState, useEffect } from 'react';
import { decryptBytes } from '../utils/crypto';

export interface MessageType {
  role: string;
  date: string;
  content: string;
  userId: string;
  categoria?: string;
  idioma_respuesta?: string;
  tipoUsuario?: string;
}

export interface DateGroupType {
  date: string;
  messages: MessageType[];
}

// Configurar API con variables de entorno
// Endpoint fijo para procesar conversaciones con su code
const API_BASE = 'https://headspringchatbot.azurewebsites.net/api/procesar_conversaciones?code=qI6DmJpMP3tLLLnjOy0gXjxIwTKKfJzLogsQKIopL0voAzFuit0o9w==&accion=';

export function useConversations(programCode: string, isAdmin: boolean) {
  const [activePrograms, setActivePrograms] = useState<string[]>([]);
  const [inactivePrograms, setInactivePrograms] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [conversations, setConversations] = useState<Record<string, DateGroupType[]>>({});
  const [loadingAssistants, setLoadingAssistants] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);

  useEffect(() => {
    fetch(API_BASE + 'get_asistentes')
      .then(res => {
        if (!res.ok) throw new Error(`Error HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setActivePrograms(data.programas_activos || []);
        setInactivePrograms(data.programas_no_activos || []);
        setLoadingAssistants(false);
      })
      .catch(err => {
        console.error('Error cargando programas:', err);
        setLoadingAssistants(false);
      });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingConversation(true);
    fetch(`${API_BASE}get_conversacion_asistente&asistente=${selected}`)
      .then(async res => {
        const text = await res.text();
        if (!res.ok) throw new Error(text);
        // Desencriptar respuesta
        const decrypted = decryptBytes(text, process.env.REACT_APP_FERNET_KEY!);
        return JSON.parse(decrypted);
      })
      .then(data => {
        // Data raw: agrupado por userId
        const raw = data[selected] || data;
        // Aplanar mensajes añadiendo userId
        const all: MessageType[] = [];
        Object.entries(raw).forEach(([uid, msgs]) => {
          (msgs as any[]).forEach(m => {
            all.push({
              userId: uid,
              role: m.role,
              date: m.date,
              content: m.content,
              categoria: m.categoria,
              idioma_respuesta: m.idioma_respuesta,
              tipoUsuario: m.TipoUsuario,
            });
          });
        });
        // Filtrar por programa si no es admin
        const filtered = isAdmin ? all : all.filter(m => m.userId === programCode);
        // Ordenar descendente por fecha
        filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        // Agrupar por día (ISO YYYY-MM-DD)
        const byDate: Record<string, MessageType[]> = {};
        filtered.forEach(m => {
          const key = new Date(m.date).toISOString().slice(0, 10);
          if (!byDate[key]) byDate[key] = [];
          byDate[key].push(m);
        });
        // Crear array de DateGroupType
        const dateGroups: DateGroupType[] = Object.keys(byDate)
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
          .map(dateKey => ({ date: dateKey, messages: byDate[dateKey] }));
        setConversations(prev => ({ ...prev, [selected]: dateGroups }));
        setLoadingConversation(false);
      });
  }, [selected, programCode, isAdmin]);

  const addMessageToConversation = (userId: string, message: string, date: string) => {
    const newMessage: MessageType = {
      role: 'admin',
      date: new Date().toISOString(), // La fecha del mensaje sigue siendo la actual
      content: message,
      userId,
    };

    setConversations(prev => {
      const newConversations = { ...prev };
      const currentConvs = newConversations[selected] || [];
      
      let dateGroup = currentConvs.find(g => g.date === date);

      if (dateGroup) {
        dateGroup.messages.push(newMessage);
      } else {
        // Este caso no debería ocurrir si se contacta desde un grupo existente
        dateGroup = { date, messages: [newMessage] };
        currentConvs.unshift(dateGroup);
      }

      newConversations[selected] = [...currentConvs];
      return newConversations;
    });
  };

  return {
    activePrograms,
    inactivePrograms,
    selected,
    conversations,
    loadingAssistants,
    loadingConversation,
    setSelected,
    addMessageToConversation,
  };
} 